import opentype, { type Font } from 'opentype.js';

import type { FontFileMetadata } from './font-file-metadata';

export type { FontFileMetadata } from './font-file-metadata';

// opentype.js supports lowMemory, but its published types omit this option.
const parseOpenType: (bytes: ArrayBuffer, options: { lowMemory: boolean }) => Font = opentype.parse;

function detectFontType(bytes: ArrayBuffer): FontFileMetadata['fileType'] {
	switch (new DataView(bytes).getUint32(0)) {
		case 0x00010000:
		case 0x74727565:
			return 'ttf';
		case 0x4f54544f:
			return 'otf';
		case 0x774f4646:
			return 'woff';
		case 0x774f4632:
			return 'woff2';
		default:
			throw new Error('Unsupported font signature.');
	}
}

function localizedName(names: Record<string, string> | undefined): string {
	const values = [names?.en, ...Object.values(names ?? {})];
	return (
		values
			.find(value => typeof value === 'string' && value.trim())
			?.replace(/\x7f/g, '')
			.trim() ?? ''
	);
}

/** Reads metadata without browser globals or changing the original font bytes. */
export async function parseFontMetadata(bytes: ArrayBuffer, filename = ''): Promise<FontFileMetadata> {
	try {
		const fileType = detectFontType(bytes);
		let decoded = bytes;
		if (fileType === 'woff2') {
			const { default: decompress } = await import('wawoff2/decompress.js');
			decoded = Uint8Array.from(await decompress(new Uint8Array(bytes))).buffer;
		}
		const font = parseOpenType(decoded, { lowMemory: true });
		const names: Record<string, Record<string, string> | undefined> = font.tables.name;
		const fontFamily =
			localizedName(names.preferredFamily) ||
			localizedName(names.fontFamily) ||
			localizedName(names.fullName) ||
			filename;
		if (!fontFamily) throw new Error('The font has no family name.');
		const subfamily = localizedName(names.preferredSubfamily) || localizedName(names.fontSubfamily);
		return {
			fontFamily,
			weight: font.tables.os2?.usWeightClass ?? 400,
			style: subfamily.toLowerCase().includes('italic') ? 'Italic' : 'Normal',
			fileType,
		};
	} catch (cause) {
		throw new Error('Invalid or unsupported font file. Choose a valid TTF, OTF, WOFF or WOFF2 file.', { cause });
	}
}
